import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import MessageList from "./MessageList";
import EmojiPicker from "./EmojiPicker";
import "./chat.css";

const socket = io();

export const Chat = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const typingTimeout = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await axios.get("/api/users", {
          params: { currentUser: user.username },
        });
        setUsers(data);
      } catch (error) {
        console.error("Error fetching users", error);
      }
    };

    fetchUsers();
  }, [user.username]);

  useEffect(() => {
    socket.on("receive_message", (data) => {
      const belongsHere =
        (data.sender === user.username && data.receiver === currentChat) ||
        (data.sender === currentChat && data.receiver === user.username);

      if (!belongsHere) return;

      setMessages((prev) => [...prev, data]);

      // it landed in a chat I am looking at, so mark it read straight away
      if (data.sender === currentChat) {
        setIsTyping(false);
        socket.emit("mark_read", {
          sender: currentChat,
          receiver: user.username,
        });
      }
    });

    socket.on("typing", (data) => {
      if (data.sender === currentChat && data.receiver === user.username) {
        setIsTyping(true);
      }
    });

    socket.on("stop_typing", (data) => {
      if (data.sender === currentChat && data.receiver === user.username) {
        setIsTyping(false);
      }
    });

    socket.on("messages_read", (data) => {
      // my messages to the person I have open just got read
      if (data.sender === user.username && data.receiver === currentChat) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.sender === user.username ? { ...msg, read: true } : msg,
          ),
        );
      }
    });

    return () => {
      socket.off("receive_message");
      socket.off("typing");
      socket.off("stop_typing");
      socket.off("messages_read");
    };
  }, [currentChat, user.username]);

  const fetchMessages = async (receiver) => {
    try {
      const { data } = await axios.get("/api/messages", {
        params: { sender: user.username, receiver },
      });
      setMessages(data);
      setCurrentChat(receiver);
      setIsTyping(false);
      setShowEmoji(false);

      socket.emit("mark_read", { sender: receiver, receiver: user.username });
    } catch (error) {
      console.error("Error fetching messages", error);
    }
  };

  const stopTyping = () => {
    clearTimeout(typingTimeout.current);
    socket.emit("stop_typing", {
      sender: user.username,
      receiver: currentChat,
    });
  };

  const handleChange = (e) => {
    setCurrentMessage(e.target.value);
    if (!currentChat) return;

    socket.emit("typing", { sender: user.username, receiver: currentChat });

    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(stopTyping, 1500);
  };

  const addEmoji = (emoji) => {
    setCurrentMessage((prev) => prev + emoji);
    inputRef.current?.focus();
  };

  const sendMessage = () => {
    if (!currentMessage.trim() || !currentChat) return;

    socket.emit("send_message", {
      sender: user.username,
      receiver: currentChat,
      message: currentMessage,
    });

    setCurrentMessage("");
    setShowEmoji(false);
    stopTyping();
  };

  return (
    <div className="chat-container">
      <h2>Welcome, {user.username}</h2>
      <div className="chat-list">
        <h3>Chats</h3>
        {users.map((u) => (
          <div
            key={u._id}
            className={`chat-user ${
              currentChat === u.username ? "active" : ""
            }`}
            onClick={() => fetchMessages(u.username)}
          >
            {u.username}
          </div>
        ))}
      </div>
      {currentChat && (
        <div className="chat-window">
          <h5>You are chatting with {currentChat}</h5>
          <MessageList messages={messages} user={user} />

          <div className="typing-indicator">
            {isTyping ? `${currentChat} is typing...` : ""}
          </div>

          <div className="message-field">
            <button
              type="button"
              className="emoji-toggle"
              onClick={() => setShowEmoji((prev) => !prev)}
            >
              😊
            </button>
            <input
              ref={inputRef}
              type="text"
              placeholder="Type a message..."
              value={currentMessage}
              style={{ minWidth: "400px" }}
              onChange={handleChange}
              onBlur={stopTyping}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            />
            <button className="btn-prime" onClick={sendMessage}>
              Send
            </button>

            {showEmoji && <EmojiPicker onSelect={addEmoji} />}
          </div>
        </div>
      )}
    </div>
  );
};
